import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { NormalizedAddress } from '@components/AddressCapture/AddressCapture.types';
import type {
  AddressType,
  CartState,
  LeadCaptureContext,
  LeadCaptureReason,
  QualificationStatus,
  ServiceQualificationResponse,
} from '@t/serviceQualification.types';
import { CPQ_QUALIFICATION_FLOW_KEY } from '@t/session.types';

export type CustomerType = 'RESIDENTIAL' | 'BUSINESS';

export interface QualificationFlowState {
  homeIdOfQualifiedOffers: string | null;
  isCreateWorkOrderManually: boolean;
  qualificationResult: ServiceQualificationResponse | null;
  cartState?: CartState;
  addressType?: AddressType;
  qualificationStatus?: QualificationStatus;
  resolvedAddressId: string | null;
  leadCaptureContext: LeadCaptureContext | null;
  leadCaptureReason: LeadCaptureReason | null;
  capturedAddress: NormalizedAddress | null;
  onNet: string | boolean | null;
  customerType: CustomerType | null;
  /** Transient flag: resume detection failed, show error in CheckAvailabilityDialog */
  resumeDetectionFailed?: boolean;
  /** Transient flag: active work order detected, show banner in CheckAvailabilityDialog */
  hasActiveWorkOrder?: boolean;
  /** Target route for resuming an in-progress order */
  resumeRoute?: string | null;
  /** Transient flag: user chose to edit service address from checkout */
  isChangeAddressOpen?: boolean;
  /** Transient: 'open' = dialog visible, 'dismissed' = user closed without submitting */
  leadCaptureStatus?: 'open' | 'dismissed';
}

const DEFAULT_STATE: QualificationFlowState = {
  homeIdOfQualifiedOffers: null,
  isCreateWorkOrderManually: false,
  qualificationResult: null,
  resolvedAddressId: null,
  leadCaptureContext: null,
  leadCaptureReason: null,
  capturedAddress: null,
  onNet: null,
  customerType: null,
};

const STORE_VERSION = 2;

type LegacyPersistedState = Partial<QualificationFlowState> & {
  gldsHomeID?: string | null;
  gldsParentID?: string | null;
  parentHomeId?: string | null;
  resolvedHomeID?: string | null;
  homeId?: string | null;
};

// Legacy persisted shapes (v0) carried five duplicate id fields. Collapse
// to resolvedAddressId, falling back through the duplicates in the order
// useServiceQualification wrote them.
const migrateFromLegacy = (persisted: LegacyPersistedState | null | undefined): QualificationFlowState => {
  if (!persisted || typeof persisted !== 'object') return DEFAULT_STATE;
  const resolvedAddressId = persisted.resolvedAddressId ?? persisted.resolvedHomeID ?? persisted.homeId ?? null;
  return {
    ...DEFAULT_STATE,
    homeIdOfQualifiedOffers: persisted.homeIdOfQualifiedOffers ?? null,
    isCreateWorkOrderManually: persisted.isCreateWorkOrderManually ?? false,
    qualificationResult: persisted.qualificationResult ?? null,
    cartState: persisted.cartState,
    addressType: persisted.addressType,
    qualificationStatus: persisted.qualificationStatus,
    resolvedAddressId,
    leadCaptureContext: persisted.leadCaptureContext ?? null,
    leadCaptureReason: persisted.leadCaptureReason ?? null,
    capturedAddress: persisted.capturedAddress ?? null,
    onNet: persisted.onNet ?? null,
    customerType: persisted.customerType ?? null,
  };
};

interface QualificationFlowStore extends QualificationFlowState {
  setQualificationFlow: (
    updater: QualificationFlowState | ((prev: QualificationFlowState) => QualificationFlowState)
  ) => void;
}

export const useQualificationFlowStore = create<QualificationFlowStore>()(
  persist(
    (set) => ({
      ...DEFAULT_STATE,
      setQualificationFlow: (updater) => {
        set((state) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { setQualificationFlow: _, ...currentState } = state;
          const newState = typeof updater === 'function' ? updater(currentState) : updater;
          return newState;
        });
      },
    }),
    {
      name: CPQ_QUALIFICATION_FLOW_KEY,
      version: STORE_VERSION,
      migrate: (persistedState) => migrateFromLegacy(persistedState as LegacyPersistedState) as QualificationFlowState,
      partialize: (state): QualificationFlowState => ({
        homeIdOfQualifiedOffers: state.homeIdOfQualifiedOffers,
        isCreateWorkOrderManually: state.isCreateWorkOrderManually,
        qualificationResult: state.qualificationResult,
        cartState: state.cartState,
        addressType: state.addressType,
        qualificationStatus: state.qualificationStatus,
        resolvedAddressId: state.resolvedAddressId,
        leadCaptureContext: state.leadCaptureContext,
        leadCaptureReason: state.leadCaptureReason,
        capturedAddress: state.capturedAddress,
        onNet: state.onNet,
        customerType: state.customerType,
      }),
    }
  )
);

export const selectQualifiedAddressId = (s: QualificationFlowState): string | null =>
  s.qualificationResult?.serviceQualificationItem?.qualifiedAddress?.id ?? null;

export const selectReceivedAddressId = (s: QualificationFlowState): string | null =>
  s.qualificationResult?.serviceQualificationItem?.receivedAddress?.id ?? null;

// Exported for tests
export const __test__ = { migrateFromLegacy, DEFAULT_STATE, STORE_VERSION };
